import React from 'react';
import { Field } from 'ui';
import Tooltip from '~/components/Form/Tooltip';
import DetailGuidance from '~/components/ItemDetails/DetailGuidance';
import HelpButton from '~/components/Links/HelpButton';
import DetailSelector from '~/components/ListFlow/DetailSelector';
import RequiredStar from '~/components/ListFlow/RequiredStar';
import useDetailGuidance from '~/hooks/useDetailGuidance';
import {
  changeDetail,
  getListFlowCategory,
  getListFlowDetails,
} from '~/store/listFlow/slices/info';
import { getListFlowFieldErrorMap } from '~/store/listFlow/ui';
import { useAppDispatch, useAppSelector } from '~/store/store';
import { RailsDetail } from '~/typings/services/rails/item';

interface Props {
  detail: RailsDetail;
  searchable?: boolean;
}

const ListFlowDetailSelector: React.FC<Props> = ({ detail, searchable }) => {
  const dispatch = useAppDispatch();
  const category = useAppSelector(getListFlowCategory);
  const errorMap = useAppSelector(getListFlowFieldErrorMap);
  const detailsState = useAppSelector(getListFlowDetails);
  const selected = detailsState?.[detail.slug]?.selected;

  const { data: detailGuidance } = useDetailGuidance(category?.id, 'list');
  const thisDetailGuidance = detailGuidance?.find(d => d.slug === detail.slug);

  return (
    <Field
      key={detail.slug}
      id={`detail-${detail.slug}`}
      error={errorMap?.[detail.slug]}
      label={
        <span className="flex">
          <span>
            {detail.name} {detail.required && <RequiredStar />}
          </span>

          {!!thisDetailGuidance && (
            <DetailGuidance
              detail={detail}
              category={category}
              src="list-flow"
              context="list"
              guidance={thisDetailGuidance}
            />
          )}

          {selected !== 'variations' &&
            selected &&
            selected?.imageRecognitionConfidence! > 0 && (
              <Tooltip
                tooltip={
                  <div>
                    Based on the image you provided,
                    <br />
                    we are {Math.round(selected.imageRecognitionConfidence!)}%
                    confident in this accuracy
                  </div>
                }
              >
                <HelpButton />
              </Tooltip>
            )}
        </span>
      }
    >
      <DetailSelector
        id={`detail-${detail.slug}`}
        detail={detail}
        onChange={(detail, selected) =>
          dispatch(changeDetail({ detail, selected }))
        }
        clearable={!detail.required}
        searchable={searchable}
        value={detailsState?.[detail.slug]?.selected}
        variationsEnabled
      />
    </Field>
  );
};

export default ListFlowDetailSelector;
